import { useHistory, useParams } from 'react-router-dom';
import useAuth from '../../hooks/use-auth';
import { ActivitySubmission } from '../../hydra';
import { ACTIVITY_PAGE_TYPE, ActivityLayout } from '../../components/Activity';
import { ROUTES } from '../../routing';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import { Helmet } from 'react-helmet-async';
import { ACTIVITY_TYPE, ActivityProvider } from '../../contexts/activity';
import Navbar, { NAVBAR_HEIGHT } from '../../components/Navbar';
import React from 'react';
import { AIWorkflow } from '../../contexts/ai-workflow';
import AIChat from '../../components/AIChat';
import { getProjectPageTitle } from '../../routing';
import { useTeamsProject } from '../../hooks/use-hydra/teams';
import {
  createTeamsProjectAIReview,
  getTeamsProject,
  saveTeamsProject,
  validateTeamsProjectAnswerAttempt,
} from '../../api/teams';
import { useTeamsProfile } from '../../hooks/use-hydra';
import { AnswerStreamSubjectProvider } from '../../answer-stream/context';
import { ANSWER_STREAM_SUBJECT_TYPE } from '../../answer-stream/types';
const StyledNavbar = styled(Navbar)`
  position: static;
`;

const StyledAIChat = styled(AIChat)`
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px - 32px);
  bottom: calc(${NAVBAR_HEIGHT}px + 16px);
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.2);
`;

export default function TeamsProject() {
  const { getTokenSilently } = useAuth();
  const { projectSlug, companySlug } =
    useParams<{
      projectSlug: string;
      companySlug?: string;
    }>();

  const { value: teamsProfile } = useTeamsProfile();
  const projectState = useTeamsProject(
    projectSlug,
    teamsProfile?.company?.slug ?? ''
  );
  const learnerCompanySlug = teamsProfile?.company?.slug ?? '';

  const submissionId = projectState.value?.latest_submission?.id ?? -1;
  const getActivity = async () => {
    const token = await getTokenSilently();
    return getTeamsProject(token, {
      companySlug: learnerCompanySlug,
      projectSlug,
    });
  };
  const history = useHistory();

  const goToHome = async (): Promise<void> => {
    toast.info('Returning home...', {
      autoClose: 2000,
    });
    setTimeout(() => {
      history.push(ROUTES.HOME.getPath());
    }, 2000);
  };

  const breadCrumbs = [
    {
      label: 'Home',
      url: ROUTES.HOME.getPath(),
    },
    {
      label: 'Projects',
      url: ROUTES.TEAMS.PROJECTS_DASHBOARD.getPath({
        teamSlug: companySlug,
      }),
    },
    {
      label: projectState.value?.title || 'Project',
      url: null,
    },
  ];

  const saveSubmission = async (submission: ActivitySubmission) => {
    const token = await getTokenSilently();

    await saveTeamsProject(token, {
      submission,
      companySlug: learnerCompanySlug,
      projectSlug: project?.slug ?? '',
    });
  };

  const createAIReview = async ({
    activitySlug,
    submissionId,
  }: {
    activitySlug: string;
    submissionId: number;
  }) => {
    const token = await getTokenSilently();
    return createTeamsProjectAIReview(token, {
      projectSlug: activitySlug,
      sessionId: submissionId,
      companySlug: learnerCompanySlug,
    });
  };

  const { hasAIAccess } = React.useContext(AIWorkflow);

  const project = projectState.value;

  const submitAnswer = React.useCallback(
    async ({
      questionSlug,
      value,
    }: {
      questionSlug: string;
      value: string;
    }) => {
      const token = await getTokenSilently();

      await validateTeamsProjectAnswerAttempt(token, {
        value,
        submissionId,
        questionSlug,
        companySlug: learnerCompanySlug,
        projectSlug: project?.slug ?? '',
      });
    },
    [getTokenSilently, submissionId, project?.slug, learnerCompanySlug]
  );

  return (
    <PageWithAsyncData
      isLoading={projectState.loading}
      error={projectState.error}
      retry={projectState.retry}
      className={''}
    >
      {projectState.value && (
        <>
          <Helmet>
            <title>{getProjectPageTitle(projectState.value.title)}</title>
          </Helmet>
          <AnswerStreamSubjectProvider
            subject={
              submissionId > 0
                ? {
                    subjectType:
                      ANSWER_STREAM_SUBJECT_TYPE.TEAMS_PROJECT_SESSION,
                    subjectId: submissionId,
                  }
                : null
            }
          >
            <ActivityProvider
              activity={projectState?.value}
              type={ACTIVITY_TYPE.PROJECT}
            >
            <StyledNavbar breadCrumbs={breadCrumbs} />
            {hasAIAccess && project && (
              <div>
                <StyledAIChat
                  contentId={null}
                  missionId={null}
                  activityId={null}
                  projectId={project.exercise.id}
                  discussionContext={'project'}
                  shouldShowChatIcon={false}
                  answerStreamSubjectType={
                    ANSWER_STREAM_SUBJECT_TYPE.TEAMS_PROJECT_SESSION
                  }
                  answerStreamSubjectId={submissionId}
                />
              </div>
            )}
            <ActivityLayout
              type={ACTIVITY_PAGE_TYPE.TEAMS}
              goToNextActivity={goToHome}
              saveSubmission={saveSubmission}
              getActivity={getActivity}
              createAIReview={createAIReview}
              dashboardUrl={ROUTES.TEAMS.PROJECTS_DASHBOARD.getPath({
                teamSlug: companySlug,
              })}
              activityState={projectState}
              submitAnswer={submitAnswer}
            />
            </ActivityProvider>
          </AnswerStreamSubjectProvider>
        </>
      )}
    </PageWithAsyncData>
  );
}
